import { useAppDispatch, useAppSelector } from "../../../../store/types";
import Box from "@mui/material/Box";
import MediafileAutoComplete from "./MediafileAutoComplete";
import Skeleton from "@mui/material/Skeleton";
import { getClosureMsgMediaFile } from "../../../../services/closureMsg";
import { sortingCaseInSensitive } from "../utils";
import { useEffect, useState } from "react";
import styles from "../styles";
import useCss from "../../../../hooks/useCss";
import { updateLanguageId } from "../../../../slices/mediaMgtSlice";
import {
  MediaItem,
  MediaMgtDataContainer,
} from "../../../../slices/mediaMgtSlice";
import { getMediaMgtDataSelector } from "../../../../selectors/mediaMgtSelector";
import {
  getClosureMsgMediaFileInfoSelector,
  getClosureMsgMediaFileStatusSelector,
} from "../../../../selectors/closureMsgSelector";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import type { MediaFileOption, MediaFileInfo } from "./types";

interface Props {
  languageId: string | null;
  handleLanguageMediaFile: (
    value: string,
    isAudioFileChanged?: boolean,
  ) => void;
  row?: Record<string, unknown> | null;
  disabled?: boolean;
  maxWidth?: string | number;
}

const ClosureAudioConfig = ({
  languageId,
  handleLanguageMediaFile,
  row = null,
  disabled = false,
  maxWidth,
}: Props) => {
  const dispatch = useAppDispatch();
  const classes = useCss(styles);

  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);
  const closureMsgMediaFileInfo = useAppSelector(
    getClosureMsgMediaFileInfoSelector,
  ) as MediaFileInfo | null;
  const closureMsgMediaFileStatus = useAppSelector(
    getClosureMsgMediaFileStatusSelector,
  );

  const [mediaFilesOnLanguageId, setMediaFilesOnLanguageId] = useState<
    MediaFileOption[]
  >([]);
  const [mediaFileInfo, setMediaFileInfo] = useState<MediaFileInfo | null>(
    null,
  );
  const [selectedMediaFile, setSelectedMediaFile] = useState<string | null>(
    null,
  );
  const [audioFileName, setAudioFileName] = useState("");
  const [hasError, setHasError] = useState(false);

  const getMediaData = (): MediaItem[] => {
    if (!mediaMgtData) return [];
    if (Array.isArray(mediaMgtData)) return mediaMgtData as MediaItem[];
    return (mediaMgtData as MediaMgtDataContainer).data ?? [];
  };

  useEffect(() => {
    const mediaData = getMediaData();
    let filteredData: MediaItem[] = [];
    if (mediaData.length > 0) {
      if (languageId === null) {
        filteredData = mediaData.filter((item) => item.languageCode === "en");
      } else {
        filteredData = languageId
          ? mediaData.filter((item) => item.languageCode === languageId)
          : [];
      }
    }
    dispatch(updateLanguageId(languageId ?? ""));
    const result: MediaFileOption[] = filteredData.reduce<MediaFileOption[]>(
      (acc, cur) => {
        acc.push({
          value: cur.filePath as string,
          name: cur.fileName as string,
        });
        return acc;
      },
      [],
    );

    if (row) {
      const languages =
        Object.keys(row).length > 0 &&
        Object.keys(row).filter((a) => a.includes("message"));
      const mediaFileObj = languages
        ? (row[languages.find((a) => a.split("_")[1] === languageId) ?? ""] as
            | { value?: string }
            | undefined)
        : undefined;
      setSelectedMediaFile(
        mediaFileObj && mediaFileObj.value ? mediaFileObj.value : "",
      );
      if (mediaFileObj) handleMediaFileChange(mediaFileObj.value ?? "");
    }
    setMediaFilesOnLanguageId(result);
  }, [languageId, mediaMgtData]);

  useEffect(() => {
    if (closureMsgMediaFileInfo?.mediaContent) {
      setMediaFileInfo(closureMsgMediaFileInfo);
    }
  }, [closureMsgMediaFileInfo]);

  const playAudioFile = (file: string) => {
    return (
      <Box>
        {closureMsgMediaFileInfo === null &&
        closureMsgMediaFileStatus !== "success" ? (
          <Box sx={{ width: "18vw", height: 26, maxWidth }}>
            <Skeleton animation="wave" height={26} />
          </Box>
        ) : (
          <audio
            key={Math.random()}
            style={{
              height: 26,
              maxWidth: maxWidth as number | undefined,
            }}
            controls
            title={audioFileName}
          >
            {selectedMediaFile &&
            closureMsgMediaFileInfo?.languageCode === languageId ? (
              <source src={file} type="audio/wav" />
            ) : (
              <source src={undefined} type="audio/wav" />
            )}
          </audio>
        )}
      </Box>
    );
  };

  const handleMediaFileChange = async (
    value: string,
    isAudioFileChanged = false,
  ) => {
    const mediaData = getMediaData();
    const filerData = mediaData.find((item) => item.filePath === value);
    const data = {
      instanceId: filerData?.instanceId as string | undefined,
      languageCode: filerData?.languageCode as string | undefined,
      language: filerData?.language as string | undefined,
      fileName: encodeURIComponent((filerData?.filePath as string) ?? ""),
      mediaId: filerData?.mediaId as string | undefined,
      type: filerData?.type as string | undefined,
    };

    handleLanguageMediaFile(value, isAudioFileChanged);
    setSelectedMediaFile(value);
    setAudioFileName((filerData?.fileName as string) ?? "");

    if (value) {
      await dispatch(
        getClosureMsgMediaFile(
          data as Parameters<typeof getClosureMsgMediaFile>[0],
        ),
      );
    }
  };

  const handleAudioFileValueChange = (value: string | null) => {
    handleMediaFileChange(value ?? "", true);
  };

  const handleErrorChange = (errorState: boolean) => {
    setHasError(errorState);
  };

  return (
    <Box className={classes.audioFileContainer}>
      <Box>
        <MediafileAutoComplete
          label={<SbuxTranslate>{"Audio File"}</SbuxTranslate>}
          data={sortingCaseInSensitive(mediaFilesOnLanguageId)}
          handleSelectChange={handleAudioFileValueChange}
          defaultValue={selectedMediaFile ?? ""}
          value={selectedMediaFile}
          disabled={disabled}
          required={true}
          onErrorChange={handleErrorChange}
        />
      </Box>

      <Box className={`${hasError ? classes.playerError : classes.player}`}>
        {mediaFileInfo?.mediaContent &&
          playAudioFile(mediaFileInfo.mediaContent)}
      </Box>
    </Box>
  );
};

export default ClosureAudioConfig;
